import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { NotificationCenter } from './NotificationCenter';
import Logo, { LogoIcon } from '../../components/Logo';
import {
  LayoutDashboard,
  TrendingUp,
  Users,
  BookOpen,
  DollarSign,
  Megaphone,
  UserCheck,
  Settings,
  Activity,
  Brain,
  HeartHandshake,
  Bell,
  Shield,
  LogOut,
  ChevronDown,
  Menu,
  X,
  Tag,
  UserCog,
  Zap,
  Cpu,
  Newspaper,
  Compass,
} from 'lucide-react';

const navItems = [
  { path: '/admin', label: 'Mission Control', icon: LayoutDashboard, permission: 'view_all_dashboards', exact: true },
  { path: '/admin/executive', label: 'Executive Overview', icon: TrendingUp, permission: 'view_all_dashboards' },
  { path: '/admin/user-management', label: 'User Management', icon: UserCog, permission: 'view_user_activity' },
  { path: '/admin/activity', label: 'Activity', icon: Zap, permission: 'view_all_dashboards' },
  { path: '/admin/growth', label: 'Growth Analytics', icon: TrendingUp, permission: 'view_growth_analytics' },
  { path: '/admin/users', label: 'User Behavior', icon: Users, permission: 'view_user_activity' },
  { path: '/admin/books', label: 'Book Analytics', icon: BookOpen, permission: 'view_book_pipeline' },
  { path: '/admin/revenue', label: 'Revenue', icon: DollarSign, permission: 'view_revenue_data' },
  { path: '/admin/pricing', label: 'Pricing Management', icon: Tag, permission: 'view_revenue_data' },
  { path: '/admin/product-pricing', label: 'Product Pricing', icon: Tag, permission: 'view_product_pricing' },
  { path: '/admin/marketing', label: 'Marketing', icon: Megaphone, permission: 'view_user_acquisition' },
  { path: '/admin/discover-content', label: 'Discover Content', icon: Compass, permission: 'manage_discover_content' },
  { path: '/admin/everbook-news', label: 'EverBook News', icon: Newspaper, permission: 'manage_everbook_news' },
  { path: '/admin/retention', label: 'Retention & Cohorts', icon: UserCheck, permission: 'view_retention' },
  { path: '/admin/operations', label: 'Operations', icon: Activity, permission: 'view_operational_metrics' },
  { path: '/admin/ai-health', label: 'AI System Health', icon: Brain, permission: 'view_ai_status' },
  { path: '/admin/support', label: 'Support & Trust', icon: HeartHandshake, permission: 'view_support_tickets' },
  { path: '/admin/content-moderation', label: 'Content Moderation', icon: Shield, permission: 'view_support_tickets' },
  { path: '/admin/alerts', label: 'Alerts & Incidents', icon: Bell, permission: 'view_system_alerts' },
  { path: '/admin/admin-users', label: 'Admin Users', icon: Shield, permission: 'manage_admin_users' },
  { path: '/admin/settings', label: 'Settings', icon: Settings, permission: 'configure_settings' },
  { path: '/admin/diagnostics', label: 'System Diagnostics', icon: Cpu },
];

export default function AdminNavigation() {
  const { admin, logout, hasPermission } = useAdminAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const filteredNavItems = navItems.filter(item => hasPermission(item.permission));

  const NavContent = () => (
    <>
      {/* Logo */}
      <div className="p-6 border-b border-slate-700/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LogoIcon size="md" />
            <div>
              <h1 className="font-bold text-white text-lg">EverBook</h1>
              <p className="text-xs text-slate-400">Admin Platform</p>
            </div>
          </div>
          <div className="hidden lg:block">
            <NotificationCenter />
          </div>
        </div>
      </div>

      {/* Navigation Items */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {filteredNavItems.map(item => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.exact}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) => `
              flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-all
              ${isActive
                ? 'bg-violet-950 text-violet-400 border border-violet-500/30'
                : 'text-slate-400 hover:text-white hover:bg-slate-800'}
            `}
          >
            <item.icon className="w-5 h-5" />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>

      {/* User Menu */}
      <div className="p-4 border-t border-slate-700/50">
        <div className="relative">
          <button
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-slate-800 hover:bg-slate-800 transition-colors"
          >
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm">
              {admin?.name?.charAt(0) || 'A'}
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-medium text-white truncate">{admin?.name || 'Admin'}</p>
              <p className="text-xs text-slate-400 capitalize">{admin?.role?.replace('_', ' ')}</p>
            </div>
            <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
          </button>

          <AnimatePresence>
            {userMenuOpen && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                className="absolute bottom-full left-0 right-0 z-[90] mb-2 overflow-hidden rounded-xl border border-slate-600 bg-[#080C10] shadow-[0_22px_55px_rgba(0,0,0,0.7)]"
              >
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-3 text-red-300 hover:bg-slate-800 transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="text-sm font-medium">Sign Out</span>
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 admin-surface-header border-b border-slate-700/50 z-40 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <LogoIcon size="sm" />
          <span className="font-bold text-white">EverBook Admin</span>
        </div>
        <div className="flex items-center gap-2">
          <NotificationCenter />
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="p-2 text-slate-400 hover:text-white"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="lg:hidden fixed inset-0 admin-surface-backdrop z-40"
            onClick={() => setMobileOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full w-64 admin-surface-nav border-r border-slate-700/50 z-50
        flex flex-col transition-transform lg:translate-x-0
        ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <NavContent />
      </aside>
    </>
  );
}
